import React, { useContext } from "react";
import { t } from "ttag";

import { constants } from "@reactivated/constants";

import { assertNever } from "@thelabnyc/thelabui/src/utils/never";

import { ReactivatedContext as Context } from "../../context";
import {
    Kilometers,
    MILES_TO_METERS,
    Meters,
    Miles,
    isoKilometers,
    isoMeters,
    isoMiles,
} from "../../models";
import { Clickable } from "../Clickables";
import { Svg } from "../Svg";

import styles from "./LocationListItem.module.scss";

const DistanceUnit = constants["server.cms.types.DistanceUnit"];

type DistanceUnit = (typeof DistanceUnit)[keyof typeof DistanceUnit];

const metersToKMs = (dist: Meters): Kilometers => {
    const unwrappedMeters = isoMeters.unwrap(dist);
    return isoKilometers.wrap(unwrappedMeters / 1000);
};

const metersToMiles = (dist: Meters): Miles => {
    const unwrappedMeters = isoMeters.unwrap(dist);
    return isoMiles.wrap(unwrappedMeters / MILES_TO_METERS);
};

const formatDist = (dist: Meters, unit: DistanceUnit): string => {
    switch (unit) {
        case DistanceUnit.KM: {
            const distKMs = isoKilometers.unwrap(metersToKMs(dist)).toFixed(1);
            return t`${distKMs} km`;
        }
        case DistanceUnit.MI: {
            const distMIs = isoMiles.unwrap(metersToMiles(dist)).toFixed(1);
            return t`${distMIs} mi`;
        }
        default: {
            return assertNever(unit);
        }
    }
};

const isDistUnit = (unit: string): unit is DistanceUnit => {
    return Object.values(DistanceUnit).includes(unit as DistanceUnit);
};

export const getPreferredDistanceUnit = (raw: string): DistanceUnit =>
    isDistUnit(raw) ? raw : DistanceUnit.MI;

export const Distance = (props: { distance: Meters; mapLink: string }) => {
    const context = useContext(Context);
    const preferredUnit = getPreferredDistanceUnit(context.DISTANCE_UNIT);
    return (
        <Clickable
            className={styles.distance}
            href={props.mapLink}
            target="_blank"
            rel="noopener noreferrer"
        >
            <p>{formatDist(props.distance, preferredUnit)}</p>
            <Svg
                name="directions-outline"
                aria-hidden="true"
                className={styles.svg}
            />
        </Clickable>
    );
};
